import path from 'path-browserify';
import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { BackLink, Icon, IconLink, IconTooltip, Page, Percent, RouteLink } from '~/components';
import { useIntegrations, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import { Tag, Tags } from '../components/HeaderStyledComponents';
import HubspotIndicator from '../components/HubspotIndicator';
import SalesforceIndicator from '../components/SalesforceIndicator';
import OpportunityStagesPopover from './OpportunityStagesPopover';
import getGradientColor from './components/gradient';

const resolveEditPath = (to, { pathname } = {}) =>
  pathname ? (pathname.endsWith(to) ? pathname : path.resolve(pathname, to)) : '';

const PipelineStatusIcon = styled(Icon)`
  font-size: 0.6rem;
  margin-right: 0.7rem;
  margin-bottom: 0.05rem;
  color: ${({ status, probability }) => {
    if (status === 'open') {
      // Get interpolated color based on probability
      return getGradientColor(probability);
    } else {
      // Use predefined colors for other statuses
      return (
        {
          won: colors.success,
          lost: colors.danger,
        }[status] || colors.warning0
      ); // Fallback to 'warning0' if status is not recognized
    }
  }};
`;

function OpportunityHeader({ opportunity, onStageChange }) {
  const location = useLocation();
  const { workspace } = useWorkspace();
  const { name, opportunityStage, company, permissions } = opportunity;
  const integrations = useIntegrations();

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/pipeline/opportunities`} />

        <Page.Info>
          <Page.Eyebrow>Opportunity Dashboard</Page.Eyebrow>
          {opportunity && <Page.Title>{name}</Page.Title>}

          <Tags>
            {company && (
              <Tag>
                <RouteLink to={`/app/${workspace.key}/pipeline/companies/${company.id}/overview`}>
                  {company.name}
                </RouteLink>
              </Tag>
            )}
            {integrations.salesforce && opportunity.salesforceOpportunity?.salesforceId && (
              <Tag>
                <SalesforceIndicator
                  id={opportunity.salesforceOpportunity?.salesforceId}
                  recordTypeName={opportunity.salesforceOpportunity?.salesforceRecordType?.name}
                />
              </Tag>
            )}
            {integrations.hubspot && opportunity.hubspotOpportunity?.hubspotId && (
              <Tag>
                <HubspotIndicator />
              </Tag>
            )}
            <Tag>
              {opportunityStage && (
                <OpportunityStagesPopover opportunity={opportunity} onStageChange={onStageChange}>
                  <PipelineStatusIcon
                    icon="circle"
                    status={opportunityStage?.statusId}
                    probability={opportunityStage?.probability}
                  />
                  <span>
                    {opportunityStage.name}&nbsp;-&nbsp;
                    <Percent value={opportunityStage.probability / 100} minimumFractionDigits={0} />
                  </span>
                </OpportunityStagesPopover>
              )}
              {opportunityStage?.description && (
                <IconTooltip
                  style={{ marginLeft: '0.5rem', marginBottom: '0.05rem' }}
                  icon="info-circle"
                  message={
                    <div>
                      <b>Stage description:</b> <i>{opportunityStage.description}</i>
                    </div>
                  }
                  color={colors.grey25}
                />
              )}
            </Tag>
          </Tags>
        </Page.Info>

        <Page.Actions>
          {permissions.edit && (
            <IconLink
              tooltip="Edit Opportunity"
              icon="pencil-alt"
              to={`${resolveEditPath('edit', location)}${location.search}`}
            />
          )}
        </Page.Actions>
      </Page.Header>
    </>
  );
}

export default OpportunityHeader;
